/**
 * GearNodeRenderer.ts
 * Draws Gear Depot markers on the isometric world layer.
 *
 * Each depot is a stacked pair of iso diamonds (gear crates) with:
 *   — purple tint when idle (matches the GEAR resource colour in the HUD)
 *   — gold tint + pulse ring when a fan is actively mining
 *   — "GEAR" label floats above
 *
 * Rendered below units (inserted into world before UnitRenderer).
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { TileMap }  from '../map/TileMap';
import type { GearNode } from '../systems/GearSystem';

const COLOUR_IDLE   = 0xba68c8; // purple — matches HUD GEAR dot
const COLOUR_ACTIVE = 0xffd700; // gold   — fan is mining
const HW = 11; // iso diamond half-width
const HH =  7; // iso diamond half-height

const LABEL_STYLE_IDLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   10,
  fill:       '#ba68c8',
  dropShadow: true, dropShadowDistance: 1, dropShadowAlpha: 0.5,
});
const LABEL_STYLE_ACTIVE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   10,
  fill:       '#ffd700',
  dropShadow: true, dropShadowDistance: 1, dropShadowAlpha: 0.6,
});

interface NodeGfx {
  crates: Graphics;
  ring:   Graphics;
  label:  Text;
}

export class GearNodeRenderer {
  private readonly container = new Container();
  private readonly nodeGfx   = new Map<string, NodeGfx>();

  constructor(world: Container, private readonly map: TileMap) {
    world.addChild(this.container);
  }

  init(nodes: GearNode[]): void {
    for (const node of nodes) {
      const crates = new Graphics();
      const ring   = new Graphics();
      const label  = new Text('GEAR', LABEL_STYLE_IDLE);
      label.anchor.set(0.5, 1.0);

      this.container.addChild(ring, crates, label);
      this.nodeGfx.set(node.id, { crates, ring, label });

      const wp = this.map.tileTopCenter(node.col, node.row);
      crates.position.set(wp.x, wp.y);
      ring.position.set(wp.x, wp.y);
      label.position.set(wp.x, wp.y - 22);
    }
  }

  render(nodes: GearNode[], elapsed: number): void {
    for (const node of nodes) {
      const gfx = this.nodeGfx.get(node.id);
      if (!gfx) continue;

      const colour = node.active ? COLOUR_ACTIVE : COLOUR_IDLE;
      const phase  = elapsed * 2.4;
      const sin    = Math.sin(phase);

      // ── Crate stack — two stacked iso diamonds ──────────────────────────
      const scale = node.active ? 1.0 + sin * 0.07 : 1.0;

      gfx.crates.clear();

      // Back (lower) crate — shifted down 5px
      const bw = HW * scale * 0.75;
      const bh = HH * scale * 0.75;
      gfx.crates.lineStyle(1, colour, 0.45);
      gfx.crates.beginFill(colour, 0.10);
      gfx.crates.moveTo(0,   -bh - 5);
      gfx.crates.lineTo(bw,    0 - 5);
      gfx.crates.lineTo(0,    bh - 5);
      gfx.crates.lineTo(-bw,   0 - 5);
      gfx.crates.closePath();
      gfx.crates.endFill();

      // Front (upper) crate
      const fw = HW * scale;
      const fh = HH * scale;
      gfx.crates.lineStyle(1.5, colour, node.active ? 0.95 : 0.65);
      gfx.crates.beginFill(colour, node.active ? 0.22 : 0.13);
      gfx.crates.moveTo(0,   -fh);
      gfx.crates.lineTo(fw,    0);
      gfx.crates.lineTo(0,    fh);
      gfx.crates.lineTo(-fw,   0);
      gfx.crates.closePath();
      gfx.crates.endFill();
      gfx.crates.lineStyle(0);

      // ── Pulse ring when mining ──────────────────────────────────────────
      gfx.ring.clear();
      if (node.active) {
        const fanBoost = Math.min(node.fanCount, 3) * 0.06;
        const alpha    = 0.35 + sin * 0.15 + fanBoost;
        const radius   = 20 + sin * 3;
        gfx.ring.lineStyle(1.5, COLOUR_ACTIVE, alpha);
        gfx.ring.drawCircle(0, 0, radius);
        gfx.ring.lineStyle(0);

        // Second, faster ring for multi-fan intensity
        if (node.fanCount >= 2) {
          const a2 = 0.25 + Math.sin(phase * 1.7) * 0.12;
          gfx.ring.lineStyle(1, COLOUR_ACTIVE, a2);
          gfx.ring.drawCircle(0, 0, radius + 7);
          gfx.ring.lineStyle(0);
        }
      }

      // ── Label ──────────────────────────────────────────────────────────
      gfx.label.style = node.active ? LABEL_STYLE_ACTIVE : LABEL_STYLE_IDLE;
    }
  }
}
